"use client";

import React from "react";
import Image, { type StaticImageData } from "next/image";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Images, Icons } from "@/app/assets/shared";

interface WhyChooseUsServicesProps {
  title2: React.ReactNode;
  description: React.ReactNode;
  servicesArray: ServiceItem[];
  ctaHeading?: string;
  ctaDescription?: string;
}

type ServiceItem = {
  icon: string | StaticImageData;
  title: string;
  description: string;
};

export default function WhyChooseUsServices({
  title2,
  description,
  servicesArray,
  ctaHeading = "Have an App Idea?",
  ctaDescription = "Our experts can help validate your concept, estimate development costs, and recommend the right technology stack.",
}: WhyChooseUsServicesProps) {
  return (
    <section className="relative bg-[#d9e2ed]">
      <div className="w-[calc(100%_-_10%)] mx-auto max-w-[2500px] flex flex-col gap-[20px] lg:py-[100px] py-[70px]">
        {/* Header row - Stacked vertically */}
        <div className="flex flex-col gap-5">
          <h2 className="clash-font font-medium leading-[120%] text-[#00040b] text-[length:var(--section-title)]">
            {title2}
          </h2>
          <p className="text-[#00040b]/70 text-[length:var(--hero-para)] leading-[160%] mt-2">
            {description}
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {servicesArray?.map((item) => (
            <div
              key={item.title}
              className="bg-white border border-[#ecf0f6] p-8 lg:p-[42px] flex flex-col gap-[60px]"
            >
              <div>
                <Image
                  src={item.icon}
                  alt={item.title}
                  width={66}
                  height={66}
                  sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
                  priority
                  fetchPriority="high"
                />
              </div>
              <div className="flex flex-col gap-5">
                <h3 className="text-[22px] lg:text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] clash-font font-medium leading-[120%] text-[#00040b]">
                  {item.title}
                </h3>
                <p
                  className="text-[18px] lg:text-[20px] leading-[140%] text-[#636364]"
                  dangerouslySetInnerHTML={{ __html: item.description ?? "" }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div
          className="relative mt-2 overflow-hidden  bg-[#225BD4] px-5 sm:px-8 lg:px-10 py-6 sm:py-7 lg:py-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5 sm:gap-6"
          style={{
            backgroundImage: `url(${Images?.comparisonBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {/* Text */}
          <div className="relative z-[1] max-w-[1100px]">
            <h3 className="text-white text-[18px] sm:text-[20px] lg:text-[26px] font-semibold leading-[130%]">
              {ctaHeading}
            </h3>

            <p className="clash-font mt-2 text-white/85 text-[14px] sm:text-[17px] lg:text-[24px] leading-[150%] lg:leading-[160%]">
              {ctaDescription}
            </p>
          </div>

          {/* Button */}
          <div className="relative z-[1] shrink-0 w-full lg:w-auto">
            <PrimaryBtn
              text="Get Free Project Estimation"
              iconSrc={Icons?.chevronBlue}
              dark={true}
              href="/contact-us"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
